import React, { useState, useEffect } from 'react';
import { getSalesQuantityReport, getUsers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Download, Users, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/PageHeader';
import * as XLSX from 'xlsx';

const SalesQuantityReports = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [representatives, setRepresentatives] = useState([]);
  const [selectedRep, setSelectedRep] = useState('all');
  
  // Tarih filtreleri
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [startDate, setStartDate] = useState(formatDate(firstDayOfMonth));
  const [endDate, setEndDate] = useState(formatDate(today));
  
  // Kategori açılır/kapanır durumları
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    loadRepresentatives();
  }, []);

  useEffect(() => {
    loadReportData();
  }, [startDate, endDate, selectedRep]);

  const loadRepresentatives = async () => {
    try {
      const users = await getUsers();
      const reps = users.filter(u => u.role === 'temsilci' || u.role === 'abonelik');
      setRepresentatives(reps);
    } catch (error) {
      console.error('Temsilciler yüklenemedi:', error);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const data = await getSalesQuantityReport(startDate, endDate, selectedRep);
      setReportData(data);
    } catch (error) {
      toast.error('Rapor yüklenirken hata oluştu');
      console.error('Rapor yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const exportToExcel = () => {
    if (!reportData) {
      toast.error('Dışa aktarılacak veri yok');
      return;
    }

    // Özet sayfası
    const summaryData = Object.entries(reportData.category_totals).map(([category, total]) => ({
      'Kategori': category,
      'Toplam Adet': total
    }));

    // Temsilci bazlı detay sayfası
    const repDetailData = [];
    Object.entries(reportData.rep_category_totals || {}).forEach(([rep, categories]) => {
      Object.entries(categories).forEach(([category, count]) => {
        if (count > 0) {
          repDetailData.push({
            'Temsilci': rep,
            'Kategori': category,
            'Adet': count
          });
        }
      });
    });

    // Ürün detayları sayfası
    const productDetailData = [];
    Object.entries(reportData.products_by_category || {}).forEach(([category, products]) => {
      products.forEach(product => {
        productDetailData.push({
          'Kategori': category,
          'Ürün Adı': product.product_name,
          'Toplam Adet': product.total_quantity
        });
      });
    });

    const wb = XLSX.utils.book_new();
    
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Özet');
    
    if (repDetailData.length > 0) {
      const repWs = XLSX.utils.json_to_sheet(repDetailData);
      XLSX.utils.book_append_sheet(wb, repWs, 'Temsilci Bazlı');
    }
    
    if (productDetailData.length > 0) {
      const productWs = XLSX.utils.json_to_sheet(productDetailData);
      XLSX.utils.book_append_sheet(wb, productWs, 'Ürün Detayları');
    }

    XLSX.writeFile(wb, `satis_adet_raporu_${startDate}_${endDate}.xlsx`);
    toast.success('Rapor başarıyla indirildi');
  };

  // Kategori renkleri
  const categoryColors = {
    'Arvento Peşin': 'bg-blue-500',
    'Arvento Operatör': 'bg-green-500',
    'Arvento Taksitli': 'bg-purple-500',
    'M2M': 'bg-yellow-500',
    'Sensörler': 'bg-orange-500',
    'Kamera Ürünleri': 'bg-pink-500',
    'Diğer': 'bg-gray-500'
  };

  const categoryBgColors = {
    'Arvento Peşin': 'bg-blue-50 border-blue-200',
    'Arvento Operatör': 'bg-green-50 border-green-200',
    'Arvento Taksitli': 'bg-purple-50 border-purple-200',
    'M2M': 'bg-yellow-50 border-yellow-200',
    'Sensörler': 'bg-orange-50 border-orange-200',
    'Kamera Ürünleri': 'bg-pink-50 border-pink-200',
    'Diğer': 'bg-gray-50 border-gray-200'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Rapor yükleniyor...</p>
        </div>
      </div>
    );
  }

  const totalAllCategories = reportData ? Object.values(reportData.category_totals).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Satış Adet Raporları"
        subtitle="Tüm satış adetleri Temsilci Ürünleri tablosundan alınmaktadır"
      />

      {/* Filtreler */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
        <div className="flex flex-col gap-4">
          {/* Hızlı Filtre Butonları */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                setStartDate(formatDate(lastMonth));
                setEndDate(formatDate(lastMonthEnd));
              }}
              variant="outline"
              size="sm"
              className="bg-gray-50 hover:bg-gray-100"
            >
              Geçen Ay
            </Button>
            <Button
              onClick={() => {
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                setStartDate(formatDate(firstDay));
                setEndDate(formatDate(today));
              }}
              variant="outline"
              size="sm"
              className="bg-green-50 hover:bg-green-100"
            >
              Bu Ay
            </Button>
            <Button
              onClick={() => {
                const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
                setStartDate(formatDate(firstDayOfYear));
                setEndDate(formatDate(today));
              }}
              variant="outline"
              size="sm"
              className="bg-blue-50 hover:bg-blue-100"
            >
              Bu Yıl
            </Button>
            <Button
              onClick={() => {
                setStartDate('2020-01-01');
                setEndDate(formatDate(today));
              }}
              variant="outline"
              size="sm"
              className="bg-purple-50 hover:bg-purple-100"
            >
              Tüm Zamanlar
            </Button>
          </div>
          
          {/* Tarih ve Filtre Seçiciler */}
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600 z-10">Başlangıç</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>
              <span className="text-gray-400 hidden sm:block">—</span>
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600 z-10">Bitiş</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>
            </div>

            {/* Temsilci Filtresi */}
            <Select value={selectedRep} onValueChange={setSelectedRep}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="rep-filter-select">
                <SelectValue placeholder="Temsilci Seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {representatives.map(rep => (
                  <SelectItem key={rep.id} value={rep.username}>{rep.full_name || rep.username}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Excel Export */}
            <Button
              onClick={exportToExcel}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel İndir
            </Button>
          </div>
        </div>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {reportData && Object.entries(reportData.category_totals).map(([category, total]) => (
          <div
            key={category}
            className={`rounded-xl p-4 border ${categoryBgColors[category] || 'bg-gray-50 border-gray-200'}`}
            data-testid={`category-card-${category.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-3 h-3 rounded-full ${categoryColors[category] || 'bg-gray-500'} mb-2`}></div>
            <p className="text-xs text-gray-600 font-medium truncate">{category}</p>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
          </div>
        ))}
      </div>

      {/* Genel Toplam */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm">Toplam Satış Adedi</p>
            <p className="text-4xl font-bold">{totalAllCategories}</p>
          </div>
          <Package className="w-12 h-12 text-blue-200" />
        </div>
      </div>

      {/* Ana Tablo - Tek Tablo Halinde Tüm Kategoriler */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 md:p-6 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Kategori Bazlı Satış Adetleri
          </h3>
          <p className="text-sm text-gray-500 mt-1">Tüm adetler Temsilci Ürünleri tablosundaki satış kayıtlarından alınmaktadır</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Kategori</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Toplam Adet</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Ürün Sayısı</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Detay</th>
              </tr>
            </thead>
            <tbody>
              {reportData && Object.entries(reportData.category_totals)
                .filter(([_, total]) => total > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([category, total]) => {
                  const products = reportData.products_by_category?.[category] || [];
                  const isExpanded = expandedCategories[category];
                  
                  return (
                    <React.Fragment key={category}>
                      <tr 
                        className={`border-b hover:bg-gray-50 cursor-pointer ${categoryBgColors[category]?.split(' ')[0] || ''}`}
                        onClick={() => toggleCategory(category)}
                        data-testid={`category-row-${category.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${categoryColors[category] || 'bg-gray-500'}`}></div>
                            <span className="font-medium text-gray-900">{category}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold">
                            {total}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">
                          {products.length} ürün
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button variant="ghost" size="sm">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                      
                      {/* Ürün Detayları - Genişletildiğinde Göster */}
                      {isExpanded && products.length > 0 && (
                        <tr>
                          <td colSpan="4" className="px-0 py-0">
                            <div className="bg-gray-50 px-6 py-4 border-b">
                              <table className="w-full">
                                <thead>
                                  <tr className="text-xs text-gray-500 border-b">
                                    <th className="px-3 py-2 text-left">Ürün Adı</th>
                                    <th className="px-3 py-2 text-center">Toplam Adet</th>
                                    <th className="px-3 py-2 text-left">Temsilci Dağılımı</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {products.map((product, idx) => (
                                    <tr key={idx} className="border-b border-gray-100 last:border-b-0 hover:bg-white">
                                      <td className="px-3 py-2 text-sm text-gray-800">{product.product_name}</td>
                                      <td className="px-3 py-2 text-center">
                                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                                          {product.total_quantity}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-xs text-gray-600">
                                        {Object.entries(product.by_rep || {})
                                          .sort((a, b) => b[1] - a[1])
                                          .slice(0, 5)
                                          .map(([rep, count]) => `${rep}: ${count}`)
                                          .join(', ')}
                                        {Object.keys(product.by_rep || {}).length > 5 && '...'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              
              {/* Toplam Satırı */}
              <tr className="bg-blue-50 font-bold border-t-2 border-blue-300">
                <td className="px-4 py-3 text-gray-900">GENEL TOPLAM</td>
                <td className="px-4 py-3 text-center">
                  <span className="bg-blue-200 text-blue-900 px-4 py-1 rounded-full text-lg font-bold">
                    {totalAllCategories}
                  </span>
                </td>
                <td className="px-4 py-3 text-center text-gray-700">
                  {reportData ? Object.values(reportData.products_by_category || {}).reduce((acc, arr) => acc + arr.length, 0) : 0} ürün
                </td>
                <td className="px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Temsilci Bazlı Özet Tablosu */}
      {reportData && Object.keys(reportData.rep_category_totals || {}).length > 0 && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 md:p-6 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Temsilci Bazlı Satış Adetleri
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Temsilci</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold">Arvento Peşin</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold">Operatör</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold">Taksitli</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold">M2M</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold">Sensör</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold">Kamera</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold bg-blue-700">TOPLAM</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(reportData.rep_category_totals)
                  .sort((a, b) => {
                    const totalA = Object.values(a[1]).reduce((sum, v) => sum + v, 0);
                    const totalB = Object.values(b[1]).reduce((sum, v) => sum + v, 0);
                    return totalB - totalA;
                  })
                  .map(([rep, categories]) => {
                    const repTotal = Object.values(categories).reduce((sum, v) => sum + v, 0);
                    if (repTotal === 0) return null;
                    
                    return (
                      <tr key={rep} className="border-b hover:bg-gray-50" data-testid={`rep-row-${rep}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{rep}</td>
                        <td className="px-3 py-3 text-center">
                          {categories['Arvento Peşin'] > 0 && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                              {categories['Arvento Peşin']}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {categories['Arvento Operatör'] > 0 && (
                            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                              {categories['Arvento Operatör']}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {categories['Arvento Taksitli'] > 0 && (
                            <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                              {categories['Arvento Taksitli']}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {categories['M2M'] > 0 && (
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                              {categories['M2M']}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {categories['Sensörler'] > 0 && (
                            <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                              {categories['Sensörler']}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {categories['Kamera Ürünleri'] > 0 && (
                            <span className="bg-pink-100 text-pink-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                              {categories['Kamera Ürünleri']}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center bg-blue-50">
                          <span className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                            {repTotal}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                
                {/* Toplam Satırı */}
                <tr className="bg-purple-50 font-bold border-t-2 border-purple-300">
                  <td className="px-4 py-3 text-gray-900">TOPLAM</td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-blue-200 text-blue-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      {reportData.category_totals['Arvento Peşin'] || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-green-200 text-green-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      {reportData.category_totals['Arvento Operatör'] || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-purple-200 text-purple-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      {reportData.category_totals['Arvento Taksitli'] || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      {reportData.category_totals['M2M'] || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-orange-200 text-orange-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      {reportData.category_totals['Sensörler'] || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="bg-pink-200 text-pink-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      {reportData.category_totals['Kamera Ürünleri'] || 0}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center bg-blue-100">
                    <span className="bg-blue-700 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {totalAllCategories}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesQuantityReports;
